import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Button,
  Alert,
  Collapse,
} from '@mui/material';
import {
  VideoFile as VideoFileIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  CloudUpload as CloudUploadIcon,
  Refresh as RefreshIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { ProgressTrackerProps, UploadStatus } from '../../types';

const ProgressTracker: React.FC<ProgressTrackerProps> = ({
  uploads,
  onCancel,
  onRetry,
}) => {
  const [expanded, setExpanded] = React.useState(true);

  // Calculate overall statistics
  const totalUploads = uploads.length;
  const completedUploads = uploads.filter(u => u.status === UploadStatus.COMPLETED).length;
  const failedUploads = uploads.filter(u => u.status === UploadStatus.ERROR).length;
  const activeUploads = uploads.filter(u => 
    u.status === UploadStatus.UPLOADING || u.status === UploadStatus.PROCESSING
  ).length;

  const formatFileSize = (bytes: number): string => {
    // Handle invalid inputs
    if (bytes === null || bytes === undefined || isNaN(bytes) || bytes < 0) {
      return '0 Bytes';
    }
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  

  const getStatusIcon = (status: UploadStatus) => {
    switch (status) {
      case UploadStatus.COMPLETED:
        return <CheckCircleIcon color="success" />;
      case UploadStatus.ERROR:
        return <ErrorIcon color="error" />;
      case UploadStatus.UPLOADING:
      case UploadStatus.PROCESSING:
        return <CloudUploadIcon color="primary" />;
      case UploadStatus.CANCELLED:
        return <CancelIcon color="disabled" />;
      default:
        return <VideoFileIcon color="disabled" />;
    }
  };

  const getStatusColor = (status: UploadStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case UploadStatus.COMPLETED:
        return 'success';
      case UploadStatus.ERROR:
        return 'error';
      case UploadStatus.UPLOADING:
        return 'primary';
      case UploadStatus.PROCESSING:
        return 'info';
      case UploadStatus.CANCELLED:
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: UploadStatus): string => {
    switch (status) {
      case UploadStatus.IDLE:
        return 'Waiting';
      case UploadStatus.UPLOADING:
        return 'Uploading';
      case UploadStatus.PROCESSING:
        return 'Processing';
      case UploadStatus.COMPLETED:
        return 'Completed';
      case UploadStatus.ERROR:
        return 'Failed';
      case UploadStatus.CANCELLED:
        return 'Cancelled';
      default:
        return 'Unknown';
    }
  };

  if (totalUploads === 0) {
    return null;
  }

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Upload Progress ({completedUploads}/{totalUploads})
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Status chips */}
            {activeUploads > 0 && (
              <Chip
                size="small"
                label={`${activeUploads} active`}
                color="primary"
                variant="outlined"
              />
            )}
            {failedUploads > 0 && (
              <Chip
                size="small"
                label={`${failedUploads} failed`}
                color="error"
                variant="outlined"
              />
            )}
            {completedUploads > 0 && (
              <Chip
                size="small"
                label={`${completedUploads} completed`}
                color="success"
                variant="outlined"
              />
            )}
            
            {/* Expand/Collapse button */}
            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        </Box>

        {/* Overall progress bar for active uploads */}
        {activeUploads > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Overall Progress
            </Typography>
            <LinearProgress
              variant="determinate"
              value={totalUploads > 0 && !isNaN(completedUploads) && !isNaN(totalUploads) ? (completedUploads / totalUploads) * 100 : 0}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        )}

        {/* Upload list */}
        <Collapse in={expanded}>
          <List dense>
            {uploads.map((upload) => (
              <ListItem key={upload.sessionId} divider>
                <ListItemIcon>
                  {getStatusIcon(upload.status)}
                </ListItemIcon>
                
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        {upload.filename || 'Unnamed File'}
                      </Typography>
                      <Chip
                        size="small"
                        label={getStatusText(upload.status)}
                        color={getStatusColor(upload.status)}
                        variant="outlined"
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      {/* Progress bar for individual upload */}
                      {(upload.status === UploadStatus.UPLOADING || upload.status === UploadStatus.PROCESSING) && (
                        <LinearProgress
                          variant={upload.status === UploadStatus.PROCESSING ? "indeterminate" : "determinate"}
                          value={isNaN(upload.progress) ? 0 : Math.min(100, Math.max(0, upload.progress))}
                          sx={{ mb: 1, height: 4, borderRadius: 2 }}
                        />
                      )}
                      
                      {/* Upload details */}
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(upload.uploadedBytes)} / {formatFileSize(upload.totalBytes)}
                        </Typography>
                        
                        {upload.status === UploadStatus.UPLOADING && upload.progress > 0 && !isNaN(upload.progress) && (
                          <Typography variant="caption" color="text.secondary">
                            {Math.round(upload.progress)}%
                          </Typography>
                        )}
                        
                        {upload.status === UploadStatus.PROCESSING && (
                          <Typography variant="caption" color="primary">
                            Processing video...
                          </Typography>
                        )}
                      </Box>
                      
                      {/* Error message */}
                      {upload.error && (
                        <Alert severity="error" sx={{ mt: 1, py: 0 }}>
                          <Typography variant="caption">
                            {upload.error}
                          </Typography>
                        </Alert>
                      )}
                    </Box>
                  }
                />
                
                <ListItemSecondaryAction>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {/* Cancel button for active uploads */}
                    {(upload.status === UploadStatus.UPLOADING || upload.status === UploadStatus.PROCESSING) && onCancel && (
                      <IconButton
                        size="small"
                        onClick={() => onCancel(upload.sessionId)}
                        color="error"
                        title="Cancel upload"
                      >
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    )}
                    
                    {/* Retry button for failed uploads */}
                    {upload.status === UploadStatus.ERROR && onRetry && (
                      <IconButton
                        size="small"
                        onClick={() => onRetry(upload.sessionId)}
                        color="primary"
                        title="Retry upload"
                      >
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Collapse>

        {/* Action buttons */}
        {(failedUploads > 0 || activeUploads > 0) && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            {/* Cancel all active uploads */}
            {activeUploads > 0 && onCancel && (
              <Button
                size="small"
                color="error"
                onClick={() => {
                  uploads
                    .filter(u => u.status === UploadStatus.UPLOADING || u.status === UploadStatus.PROCESSING)
                    .forEach(u => onCancel(u.sessionId));
                }}
              >
                Cancel All
              </Button>
            )}
            
            {/* Retry all failed uploads */}
            {failedUploads > 0 && onRetry && (
              <Button
                size="small"
                color="primary"
                onClick={() => {
                  uploads
                    .filter(u => u.status === UploadStatus.ERROR)
                    .forEach(u => onRetry(u.sessionId));
                }}
              >
                Retry Failed
              </Button>
            )}
          </Box>
        )}

        {/* Success message */}
        {totalUploads > 0 && completedUploads === totalUploads && failedUploads === 0 && (
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">
              All {totalUploads} upload{totalUploads > 1 ? 's' : ''} completed successfully!
            </Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default ProgressTracker; 